import { Location } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NgbModal, NgbTypeahead } from "@ng-bootstrap/ng-bootstrap";
import { DateTime } from "luxon";
import {
  BehaviorSubject,
  forkJoin,
  interval,
  merge,
  Observable,
  Subject,
} from "rxjs";
import {
  debounce,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
} from "rxjs/operators";
import { IProject } from "src/app/admin/projects/shared/models/IProject";
import { ProjectService } from "src/app/admin/shared/services/project.service";
import { IWorkOrder } from "src/app/admin/work-orders/models/IWorkOrder";
import { WorkOrderService } from "src/app/admin/work-orders/work-order.service";
import { TimePeriodsService } from "src/app/time-periods/time-periods.service";
import { ITimeEntry } from "../models/ITimeEntry";
import { TimetrackingService } from "../timetracking.service";

@Component({
  selector: "app-time-edit",
  templateUrl: "./time-edit.component.html",
  styleUrls: ["./time-edit.component.scss"],
})
export class TimeEditComponent implements OnInit {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private savingSubject = new BehaviorSubject<boolean>(false);

  loading$ = this.loadingSubject.asObservable();
  saving$ = this.savingSubject.asObservable();

  @ViewChild("projectSearch", { static: false }) projectSearch: NgbTypeahead;
  @ViewChild("editing", { static: false }) editingModal: ElementRef;

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  canAdd = true;
  affectedTimeEntries: string[] = [];

  workDay: DateTime;
  userId: number; // TODO if we are passing this in, we want to make sure not everyone can just edit everyones time
  timeEntry: ITimeEntry;
  startingProjectId: number;
  startingWorkorderId: number;

  allActiveProjects: IProject[] = [];
  recentActiveProjects: IProject[] = [];
  projectWorkOrders: IWorkOrder[] = [];

  timeEntryForm: FormGroup;
  manuallyChooseWorkOrder = false;

  get projectControl() {
    return this.timeEntryForm.controls.project;
  }
  get workOrderControl() {
    return this.timeEntryForm.controls.workOrder;
  }
  get workDayControl() {
    return this.timeEntryForm.controls.workDay;
  }
  get forThirdPartyControl() {
    return this.timeEntryForm.controls.forThirdParty;
  }
  get hoursControl() {
    return this.timeEntryForm.controls.hours;
  }
  get minutesControl() {
    return this.timeEntryForm.controls.minutes;
  }
  get totalMinutesControl() {
    return this.timeEntryForm.controls.totalMinutes;
  }
  get manuallyChooseControl() {
    return this.timeEntryForm.controls.manuallyChooseWorkOrder;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private timeTrackingService: TimetrackingService,
    private timePeriodService: TimePeriodsService,
    private projectService: ProjectService,
    private workOrderService: WorkOrderService,
    private location: Location,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.createForm();
    this.getDataForForm();
    this.wireProjectValueChanges();
  }
  ngOnDestroy() {
    // make sure any modals are dismissed
    this.modalService.dismissAll();
  }
  save() {
    if (!this.timeEntryForm.valid) {
      this.timeEntryForm.markAsDirty();
      this.timeEntryForm.markAllAsTouched();
      return;
    }
    const formValues = this.timeEntryForm.value;
    const selectedProject: IProject = formValues.project;
    const selectedWorkOrder: IWorkOrder = formValues.workOrder;
    const entry: ITimeEntry = {
      id: this.timeEntry.id,
      userId: this.timeEntry.userId,
      entryDate: this.timeEntry.entryDate.toUTC(),
      entryDateIso: this.timeEntry.entryDateIso,
      projectId: selectedProject.id,
      workOrderId: formValues.manuallyChooseWorkOrder
        ? selectedWorkOrder.id
        : null,
      workDay: formValues.workDay,
      workDayIso: formValues.workDay,
      forThirdParty: formValues.forThirdPartyControl,
      timeSpentMinutes: formValues.hours * 60 + formValues.minutes,
      isLocked: false,
      usesBuiltInTime: false,
    };
    this.savingSubject.next(true);
    this.timeTrackingService.saveTimeEntry(entry).subscribe(
      (response) => {
        this.affectedTimeEntries = response;
        this.editModal();
        this.goBack();
      },
      (error) => {
        this.savingSubject.next(false);
        alert(`Error saving time entry. Please try again or report to AD team.`);
      }
    );
  }
  cancel() {
    this.goBack();
  }
  goBack() {
    this.location.back();
  }

  // Time Form code that i would like to break out into its own component
  // -- i couldnt get control pending state to propogate up to this parent component when using async validation
  // -- so until then ill just keep the code here
  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.projectSearch.isPopupOpen())
    );
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) =>
        (term === ""
          ? this.allActiveProjects.slice(0, 10)
          : this.allActiveProjects.filter(
              (p) => p.name.toLowerCase().indexOf(term.toLowerCase()) > -1
            )
        ).slice(0, 10)
      )
    );
  };
  projectSearchFormatter(project: IProject) {
    return project.name;
  }
  private createForm() {
    this.timeEntryForm = this.fb.group(
      {
        project: [null, Validators.required],
        workOrder: "",
        workDay: [
          "",
          [Validators.required, this.notFutureDateValidator()],
          this.lockedTimePeriodAsyncValidator(),
        ],

        forThirdParty: [false],
        hours: [0],
        minutes: [0],
        totalMinutes: [],
        manuallyChooseWorkOrder: [],
      },
      {
        validators: [this.PositiveTimeValidator()],
      }
    );
  }
  private notFutureDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const today = DateTime.local().startOf("day").toMillis();
      if (!control.value) {
        return { notFutureDate: { value: control.value } };
      }
      let selectedDate = DateTime.fromISO(control.value).toMillis();
      return selectedDate > today
        ? { notFutureDate: { value: control.value } }
        : null;
    };
  }
  private PositiveTimeValidator() {
    return (formGroup: FormGroup) => {
      const hoursControl = formGroup.controls.hours;
      const minutesControl = formGroup.controls.minutes;
      const totalMinutesControl = formGroup.controls.totalMinutes;

      const hours = parseInt(hoursControl.value, 10);
      const minutes = parseInt(minutesControl.value, 10);
      if ((hours <= 0 && minutes <= 0) || hours > 24 || minutes > 1440) {
        totalMinutesControl.setErrors({ positiveTime: true });
        return { notFutureDate: { value: true } };
      } else {
        // if we have other group errors this will remove them?
        totalMinutesControl.setErrors(null);
        return null;
      }
    };
  }
  private getDataForForm() {
    // TODO we grab this so that we can get the recent projects for the current user,
    // but i think i should get that from the time entry and get projects based on that
    this.userId = parseInt(this.activatedRoute.snapshot.queryParams.userid, 10);
    const entryId = parseInt(this.activatedRoute.snapshot.params.entryId, 10);
    this.timeTrackingService.getTimeEntry(entryId).subscribe((timeEntry) => {
      this.timeEntry = timeEntry;
      this.startingProjectId = timeEntry.projectId;
      this.startingWorkorderId = timeEntry.workOrderId;
      this.workDay = timeEntry.workDay;
      const hours = Math.floor(timeEntry.timeSpentMinutes / 60);
      const minutes = timeEntry.timeSpentMinutes % 60;
      this.timeEntryForm.patchValue({
        project: timeEntry.project,
        workOrder: timeEntry.workOrder,
        workDay: this.workDay.toISODate(),

        forThirdParty: false,
        hours,
        minutes,
        totalMinutes: timeEntry.timeSpentMinutes,
        manuallyChooseWorkOrder: false,
      });
    });
  }
  private wireProjectValueChanges() {
    this.projectControl.valueChanges
      .pipe(debounce(() => interval(250)))
      .subscribe((selectedProject: IProject) => {
        console.log("trying to get workorders with", selectedProject);
        if (selectedProject) {
          this.workOrderService
            .getWorkOrdersByProject(selectedProject.id)
            .subscribe((workOrders: IWorkOrder[]) => {
              this.projectWorkOrders = workOrders.filter(x => x.active);;
              let selectedWorkOrder = workOrders[0];
              if (this.startingWorkorderId) {
                selectedWorkOrder =
                  workOrders.find((workOrder) => {
                    return workOrder.id === this.startingWorkorderId;
                  }) || selectedWorkOrder;
              }
              this.workOrderControl.setValue(selectedWorkOrder);
            });
        }
      });

    //this.loadingSubject.next(true);
    forkJoin([
      this.projectService.getProjects(),
      this.projectService.getRecentProjects(this.userId),
    ]).subscribe(([projects, recentProjects]) => {
      this.allActiveProjects = projects.filter((p) => p.active);
      var editedProject = projects.find(
        (project) => project.id === this.startingProjectId
      );
      if (!editedProject.active) {
        this.allActiveProjects = this.allActiveProjects.concat(
          projects.find((project) => project.id === this.startingProjectId)
        );
      }
      this.recentActiveProjects = recentProjects.filter((p) => p.active);
      if (this.startingProjectId) {
        const selectedProject = this.allActiveProjects.find((project) => {
          return project.id === this.startingProjectId;
        });
        this.projectControl.setValue(selectedProject);
      }
    });
  }
  private lockedTimePeriodAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      const newDay = DateTime.fromISO(control.value).startOf("day");
      console.log("async validator");
      return this.timePeriodService
        .getTimePeriodExceptions(newDay, newDay)
        .pipe(
          map((timePeriods) => {
            console.log("got time periods for", newDay.toFormat("MM/dd"));
            // There should be only one time period for this one day
            const timePeriod = timePeriods[0];
            if (timePeriod) {
              const isLocked = !!timePeriod.locked;
              const hasException = !!timePeriod.timePeriodExceptions.find(
                (e) => e.userId === this.userId
              );
              return isLocked && !hasException
                ? { lockedTimePeriodAsync: true }
                : null;
            }
            return null;
          })
        );
      // return zipcodeService.fakeHttp(control.value).pipe(
      //   map((result: boolean) => result ? null : {invalidAsync: true})
      // );
    };
  }
  editModal() {
    if (this.affectedTimeEntries.length > 0) {
      this.modalService.open(this.editingModal, {
        size: "lg",
        backdrop: "static",
        keyboard: false,
        scrollable: true,
      });
    }
  }
}
